import React from 'react';
import { format } from 'date-fns';
import { RESCHEDULE_SR_LABELS as L } from '../../constants/labels';
import type { ReschedulePreviousSnapshot } from '../../hooks/useReschedule';

interface ScheduleDetails {
  technicianName: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
}

interface RescheduleConfirmationStepProps {
  previous: ReschedulePreviousSnapshot | null;
  next: ScheduleDetails & {
    technicianCode: string;
  };
}

const fmtDate = (d: Date | undefined): string =>
  d ? format(d, 'yyyy/MM/dd') : '—';
const fmtStartTime = (t: string): string =>
  t && t.trim().length > 0 ? t : '00:00';
const fmtEndTime = (t: string): string =>
  t && t.trim().length > 0 ? t : '23:59';
const fmtText = (t: string): string => (t && t.trim().length > 0 ? t : '—');

const FieldCell: React.FC<{
  label: string;
  value: string;
  valueColor: string;
}> = ({ label, value, valueColor }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      style={{
        fontFamily: 'Roboto',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        letterSpacing: 0.4,
        color: '#6a7278',
      }}
    >
      {label}
    </span>
    <span
      className="break-words"
      style={{
        fontFamily: 'Roboto',
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 500,
        color: valueColor,
      }}
    >
      {value}
    </span>
  </div>
);

const ScheduleCard: React.FC<{
  heading: string;
  details: ScheduleDetails;
  valueColor: string;
  testId: string;
}> = ({ heading, details, valueColor, testId }) => (
  <div
    className="rounded-lg border border-[#DCDDE0]"
    style={{ backgroundColor: '#FFFFFF', padding: 16 }}
    data-testid={testId}
  >
    <h3
      style={{
        fontFamily: 'Roboto',
        fontSize: 18,
        lineHeight: '24px',
        fontWeight: 500,
        letterSpacing: 0.1,
        color: '#474A4C',
        marginBottom: 16,
      }}
    >
      {heading}
    </h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
      <FieldCell
        label={L.FIELD_START_DATE}
        value={fmtDate(details.startDate)}
        valueColor={valueColor}
      />
      <FieldCell
        label={L.FIELD_END_DATE}
        value={fmtDate(details.endDate)}
        valueColor={valueColor}
      />
      <FieldCell
        label={L.FIELD_START_TIME}
        value={fmtStartTime(details.startTime)}
        valueColor={valueColor}
      />
      <FieldCell
        label={L.FIELD_END_TIME}
        value={fmtEndTime(details.endTime)}
        valueColor={valueColor}
      />
      <div className="md:col-span-2">
        <FieldCell
          label={L.FIELD_ASSIGNED_USER}
          value={fmtText(details.technicianName)}
          valueColor={valueColor}
        />
      </div>
    </div>
  </div>
);

const RescheduleConfirmationStep: React.FC<RescheduleConfirmationStepProps> = ({
  previous,
  next,
}) => {
  const previousDetails: ScheduleDetails = {
    technicianName: previous?.technicianName ?? '',
    startDate: previous?.startDate,
    endDate: previous?.endDate,
    startTime: previous?.startTime ?? '',
    endTime: previous?.endTime ?? '',
  };

  return (
    <div className="flex flex-col gap-4">
      <ScheduleCard
        heading={L.CONFIRM_SECTION_CURRENT}
        details={previousDetails}
        valueColor="#474A4C"
        testId="reschedule-confirm-current-card"
      />
      <ScheduleCard
        heading={L.CONFIRM_SECTION_UPDATED}
        details={next}
        valueColor="#474A4C"
        testId="reschedule-confirm-updated-card"
      />
    </div>
  );
};

export default RescheduleConfirmationStep;
